
import { SecuredClient } from 'Core/Api/SecuredClient';
import { GetAllPlayersByFilterCommandRequest, GetPlayerGamesByUserIdAndFilterCommandRequest, SlimPlayer } from '../../../Core/Api/Api';
import TableRequest from '../../../Core/TableUtility/Models/TableRequest';
import ITableDataResponse from '../../Forms/DataGrid/SimpleTable/Models/ITableDataResponse';

// --- Player list cache (survives navigation, cleared on page reload) ---
let cachedAllPlayers: SlimPlayer[] | null = null;
let cacheFetchPromise: Promise<SlimPlayer[]> | null = null;

export function getCachedPlayers(): SlimPlayer[] | null {
    return cachedAllPlayers;
}

export async function fetchAllPlayersWithCache(signal?: AbortSignal): Promise<SlimPlayer[]> {
    if (cachedAllPlayers) {
        return cachedAllPlayers;
    }
    if (cacheFetchPromise) {
        return cacheFetchPromise;
    }
    cacheFetchPromise = (async () => {
        try {
            const handler = new PlayerHandler();
            const request = new TableRequest();
            request.pageNumber = 1;
            const response = await handler.GetData(request);
            if (signal?.aborted) {
                throw new DOMException('Aborted', 'AbortError');
            }
            const results = response.results || [];
            cachedAllPlayers = results;
            return results;
        } finally {
            cacheFetchPromise = null;
        }
    })();
    return cacheFetchPromise;
}

export function clearPlayerListCache(): void {
    cachedAllPlayers = null;
    cacheFetchPromise = null;
}

// Types matching the backend PlayerDetailResponse DTOs
// These will be auto-generated when NSwag regenerates Api.ts

export interface PlayerHeaderInfo {
    username?: string;
    email?: string;
    joinDate?: string;
    rankName?: string;
    rankScore?: number;
    highestRating?: number;
    friendsCount?: number;
    achievementsCompleted?: number;
    totalAchievements?: number;
    vsHighScore?: number;
    singleModeBestTime?: number | null;
    lastActivity?: string;
    isOnline?: boolean;
    userStatus?: string;
    noAds?: boolean;
    moreTime?: number;
    boards?: number;
    hints?: number;
    userId?: string;
}

export interface PlayerAchievementItem {
    title?: string;
    description?: string;
    grantedAt?: string;
}

export interface DifficultyBreakdown {
    difficulty?: string;
    gamesAttempted?: number;
    gamesWon?: number;
    avgErrors?: number;
    avgHints?: number;
    bestTime?: number;
    avgTime?: number;
    worstTime?: number;
}

export interface SingleModeStats {
    gamesAttempted?: number;
    gamesWon?: number;
    gamesLost?: number;
    byDifficulty?: DifficultyBreakdown[];
}

export interface VsModeStats {
    gamesPlayed?: number;
    wins?: number;
    losses?: number;
    highestScore?: number;
    lowestScore?: number;
    avgScore?: number;
    avgHints?: number;
    avgErrors?: number;
    avgInputs?: number;
}

export interface DailyChallengeStats {
    gamesPlayed?: number;
    gamesCompleted?: number;
    gamesFailed?: number;
    bestTime?: number | null;
    worstTime?: number | null;
    avgScore?: number;
    avgHints?: number;
    avgErrors?: number;
}

export interface PurchaseItem {
    productName?: string;
    purchaseDate?: string;
    amount?: number;
}

export interface PlayerDetailResponse {
    header?: PlayerHeaderInfo;
    achievements?: PlayerAchievementItem[];
    singleModeStats?: SingleModeStats;
    vsModeStats?: VsModeStats;
    vsBotStats?: VsModeStats;
    dailyChallengeStats?: DailyChallengeStats;
    purchases?: PurchaseItem[];
}

export interface GetPlayerDetailsByUserIdCommandResponse {
    success?: boolean;
    playerDetail?: PlayerDetailResponse;
}

export class PlayerHandler {
    constructor(props?: any) { }

    GetData(tableRequest: TableRequest): Promise<ITableDataResponse<SlimPlayer>> {
        return this.getPlayerListForTable(tableRequest);
    }

    async getPlayerListForTable(request: TableRequest): Promise<ITableDataResponse<SlimPlayer>> {
        const client = new SecuredClient();
        client.showLoading = false;
        client.handleGeneralError = true;

        try {
            return await client.getPlayersByFilter(new GetAllPlayersByFilterCommandRequest(request));
        } finally {
            client.showLoading = false;
        }
    }

    async getPlayerDetails(userId: string, includeInvalidDifficulties: boolean = false): Promise<GetPlayerDetailsByUserIdCommandResponse> {
        const client = new SecuredClient();
        client.showLoading = false;
        client.handleGeneralError = true;
        return this.get(client, 'GetPlayerDetailsByUserId', {
            Id: userId,
            IncludeInvalidDifficulties: includeInvalidDifficulties,
        });
    }

    async getPlayerGames(request: GetPlayerGamesByUserIdAndFilterCommandRequest) {
        const client = new SecuredClient();
        client.showLoading = false;
        client.handleGeneralError = true;
        return client.getPlayerGamesByUserId(request);
    }

    async updatePlayerStatus(playerId: string, newStatus: string, reason: string): Promise<any> {
        const client = new SecuredClient();
        client.showLoading = true;
        client.handleGeneralError = true;
        const result = await this.post(client, 'UpdatePlayerStatus', { playerId, newStatus, reason });
        clearPlayerListCache();
        return result;
    }

    async adjustPlayerRating(playerId: string, newRankScore: number, reason: string): Promise<any> {
        const client = new SecuredClient();
        client.showLoading = true;
        client.handleGeneralError = true;
        const result = await this.post(client, 'AdjustPlayerRating', { playerId, newRankScore, reason });
        clearPlayerListCache();
        return result;
    }

    async togglePlayerAdFree(playerId: string, noAds: boolean): Promise<any> {
        const client = new SecuredClient();
        client.showLoading = true;
        client.handleGeneralError = true;
        return this.post(client, 'TogglePlayerAdFree', { playerId, noAds });
    }

    async grantPlayerItems(playerId: string, boards: number, hints: number, reason: string): Promise<any> {
        const client = new SecuredClient();
        client.showLoading = true;
        client.handleGeneralError = true;
        return this.post(client, 'GrantPlayerItems', { playerId, boards, hints, reason });
    }

    async adjustPlayerMoreTime(playerId: string, amount: number, reason: string): Promise<any> {
        const client = new SecuredClient();
        client.showLoading = true;
        client.handleGeneralError = true;
        const result = await this.post(client, 'AdjustPlayerMoreTime', { playerId, amount, reason });
        clearPlayerListCache();
        return result;
    }

    async updatePlayerItems(playerId: string, boards: number, hints: number, moreTime: number, reason: string): Promise<any> {
        const client = new SecuredClient();
        client.showLoading = true;
        client.handleGeneralError = true;
        const result = await this.post(client, 'UpdatePlayerItems', { playerId, boards, hints, moreTime, reason });
        clearPlayerListCache();
        return result;
    }

    async getUsernameHistory(playerId: string): Promise<any> {
        const client = new SecuredClient();
        client.showLoading = false;
        client.handleGeneralError = true;
        return this.get(client, 'GetUsernameHistory', { PlayerId: playerId });
    }

    async forceUsernameChange(playerId: string, newUsername: string, reason: string): Promise<any> {
        const client = new SecuredClient();
        client.showLoading = true;
        client.handleGeneralError = true;
        const result = await this.post(client, 'ForceUsernameChange', { playerId, newUsername, reason });
        clearPlayerListCache();
        return result;
    }

    async getPlayerDevices(playerId: string): Promise<any> {
        const client = new SecuredClient();
        client.showLoading = false;
        client.handleGeneralError = true;
        return this.get(client, 'GetPlayerDevices', { PlayerId: playerId });
    }

    async getPlayerRewardProgress(playerId: string): Promise<any> {
        const client = new SecuredClient();
        client.showLoading = false;
        client.handleGeneralError = true;
        return this.get(client, 'GetPlayerRewardProgress', { PlayerId: playerId });
    }

    async getPlayerSubscriptions(playerId: string): Promise<any> {
        const client = new SecuredClient();
        client.showLoading = false;
        client.handleGeneralError = true;
        return this.get(client, 'GetPlayerSubscriptions', { PlayerId: playerId });
    }

    async extendSubscription(subscriptionId: string, extendDays: number, reason: string): Promise<any> {
        const client = new SecuredClient();
        client.showLoading = true;
        client.handleGeneralError = true;
        return this.post(client, 'ExtendSubscription', { subscriptionId, extendDays, reason });
    }

    async cancelSubscription(subscriptionId: string, reason: string): Promise<any> {
        const client = new SecuredClient();
        client.showLoading = true;
        client.handleGeneralError = true;
        return this.post(client, 'CancelSubscription', { subscriptionId, reason });
    }

    private post(client: SecuredClient, action: string, body: any): Promise<any> {
        const url = `${(client as any).baseUrl}/sa/sudokuvs/admin/commands/${action}`;
        const content = JSON.stringify(body);
        return client.axiosInstance.request({
            data: content,
            method: 'POST',
            url,
            headers: {
                'Content-Type': 'application/json-patch+json',
                'Accept': 'text/plain',
            },
        }).then(r => r.data);
    }

    private get(client: SecuredClient, action: string, params?: Record<string, any>): Promise<any> {
        const query = params
            ? '?' + Object.entries(params)
                .filter(([, v]) => v !== undefined && v !== null)
                .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
                .join('&')
            : '';
        const url = `${(client as any).baseUrl}/sa/sudokuvs/admin/commands/${action}${query}`;
        return client.axiosInstance.request({
            method: 'GET',
            url,
            headers: { 'Accept': 'text/plain' },
        }).then(r => r.data);
    }
}
